/**
 * Status of an overlapping payment, replacing the old confirmedOverlap boolean.
 * Values must stay in step with the `overlapStatus` enumeration in
 * cirt-backend/src/api/overlapping-payment/content-types/overlapping-payment/schema.json
 */

export const OVERLAP_STATUS = {
  CONFIRMED_DUPLICATE_PAYMENT: "CONFIRMED_DUPLICATE_PAYMENT",
  NOT_DUPLICATE_COFUND: "NOT_DUPLICATE_COFUND",
  NOT_DUPLICATE_DOUBLE_ENTRY: "NOT_DUPLICATE_DOUBLE_ENTRY",
} as const;

export type OverlapStatus =
  (typeof OVERLAP_STATUS)[keyof typeof OVERLAP_STATUS];

export const OVERLAP_STATUS_LABELS: Record<string, string> = {
  [OVERLAP_STATUS.CONFIRMED_DUPLICATE_PAYMENT]: "Confirmed Duplicate Payment",
  [OVERLAP_STATUS.NOT_DUPLICATE_COFUND]: "Not a Duplicate Payment - Cofund",
  [OVERLAP_STATUS.NOT_DUPLICATE_DOUBLE_ENTRY]:
    "Not a Duplicate Payment - Double Entry",
};

/**
 * Compact forms for the table, where the full wording wraps over several lines
 * and crowds the column. The full labels are still used everywhere there is
 * room to read them — the select options and the view modal.
 */
export const OVERLAP_STATUS_SHORT_LABELS: Record<string, string> = {
  [OVERLAP_STATUS.CONFIRMED_DUPLICATE_PAYMENT]: "Duplicate",
  [OVERLAP_STATUS.NOT_DUPLICATE_COFUND]: "Cofund",
  [OVERLAP_STATUS.NOT_DUPLICATE_DOUBLE_ENTRY]: "Double Entry",
};

/** Ordered for the select, with the confirmed case first. */
export const OVERLAP_STATUS_OPTIONS = [
  OVERLAP_STATUS.CONFIRMED_DUPLICATE_PAYMENT,
  OVERLAP_STATUS.NOT_DUPLICATE_COFUND,
  OVERLAP_STATUS.NOT_DUPLICATE_DOUBLE_ENTRY,
].map((value) => ({ value, label: OVERLAP_STATUS_LABELS[value] }));

export const getOverlapStatusLabel = (status?: string | null): string =>
  (status && OVERLAP_STATUS_LABELS[status]) || "Not Set";

export const getOverlapStatusShortLabel = (status?: string | null): string =>
  (status && OVERLAP_STATUS_SHORT_LABELS[status]) || "Not Set";

/** Only the confirmed case is a real duplicate; used for badge colouring. */
export const isConfirmedDuplicate = (status?: string | null): boolean =>
  status === OVERLAP_STATUS.CONFIRMED_DUPLICATE_PAYMENT;
